<template>
  <div id="container" ref="boxRef"></div>
  <div id="preview"></div>
</template>

<script lang="ts">
import { defineComponent, onMounted, onUnmounted } from "vue";
import Konva from "konva";

const TARGET_IMAGE_URL =
  "https://nanchangfile.oss-cn-beijing.aliyuncs.com/arrow.png";

export default defineComponent({
  name: "App",
  data(): any {
    return {
      stage: null,
      layer: null,
      savedImages: [], // 用于保存生成的图片数据
      textBox: null, // 用于保存当前显示的文本框
      textBoxText: null, // 用于保存当前显示的文本
    };
  },
  mounted() {
    // 创建 Konva Stage
    this.stage = new Konva.Stage({
      container: "container",
      width: 1000,
      height: 500,
    });

    this.layer = new Konva.Layer();
    this.stage.add(this.layer);

    // add new images on double click or double tap
    this.stage.on("dblclick dbltap", () => {
      const pos = this.stage.getPointerPosition();
      const shape = this.layer.getIntersection(pos); // 使用 getIntersection 方法

      if (
        shape &&
        shape.className === "Image" &&
        (shape.image() as HTMLImageElement).src === TARGET_IMAGE_URL
      ) {
        // 如果点击的是目标图片，删除它
        if (this.textBox) {
          this.textBox.destroy();
          this.textBox = null;
        }

        if (this.textBoxText) {
          this.textBoxText.destroy();
          this.textBoxText = null;
        }
        shape.destroy();
        this.removeImageFromSaved(shape);
      } else {
        // 如果没有找到目标图片，添加一个新的图片
        const imageObj = new Image();
        imageObj.src = TARGET_IMAGE_URL;
        imageObj.onload = () => {
          const imageWidth = (imageObj.width * 0.2) / 2;
          const imageHeight = imageObj.height * 0.2;

          const newShape = new Konva.Image({
            x: pos.x - imageWidth,
            y: pos.y - imageHeight,
            image: imageObj,
            draggable: false,
            name: "image-" + this.layer.children.length,
            scaleX: 0.2, // 设置缩放比例
            scaleY: 0.2, // 设置缩放比例
          });

          newShape.on("click", () => {
            this.showTextBox(newShape);
          });

          this.layer.add(newShape);
          this.layer.batchDraw();

          // 保存新生成的图片数据
          this.saveImage(newShape);

          // 添加上下移动的动画
          // this.addVerticalAnimation(newShape);
        };
      }
    });
  },
  methods: {
    saveImage(image: Konva.Image) {
      this.savedImages.push({
        x: image.x(),
        y: image.y(),
        scaleX: image.scaleX(),
        scaleY: image.scaleY(),
        name: image.name(),
        src: (image.image() as HTMLImageElement).src,
      });
      localStorage.setItem("generatedImages", JSON.stringify(this.savedImages));
    },
    removeImageFromSaved(image: Konva.Image) {
      console.log(image);
      this.savedImages = this.savedImages.filter((img) => {
        return img.name !== image.name();
      });
      localStorage.setItem("generatedImages", JSON.stringify(this.savedImages));
    },
    restoreImages() {
      this.savedImages.forEach((img) => {
        const imageObj = new Image();
        imageObj.src = img.src;
        imageObj.onload = () => {
          const newShape = new Konva.Image({
            x: img.x,
            y: img.y,
            image: imageObj,
            draggable: false,
            name: img.name,
            scaleX: img.scaleX,
            scaleY: img.scaleY,
          });

          newShape.on("click", () => {
            this.showTextBox(newShape);
          });

          this.layer.add(newShape);
          this.layer.batchDraw();

          // 恢复动画
          // this.addVerticalAnimation(newShape);
        };
      });
    },
    setMap(url: string) {
      const imageObj = new Image();
      imageObj.src = url;
      imageObj.onload = () => {
        const konvaImage = new Konva.Image({
          x: 0,
          y: 0,
          image: imageObj,
          width: this.stage.width(),
          height: this.stage.height(),
        });

        this.layer.add(konvaImage);
        this.layer.batchDraw();
      };

      this.layer.draw();

      // 尝试从 localStorage 中恢复数据
      const savedData = localStorage.getItem("generatedImages");
      if (savedData) {
        this.savedImages = JSON.parse(savedData);
        this.restoreImages();
      }
    },
    addVerticalAnimation(image: Konva.Image) {
      const amplitude = 10; // 上下移动的幅度
      const duration = 2; // 动画的持续时间（秒）
      const tween = new Konva.Tween({
        node: image,
        duration: duration,
        y: image.y() + amplitude,
        easing: Konva.Easings.Linear,
        onFinish: () => {
          const reverseTween = new Konva.Tween({
            node: image,
            duration: duration,
            y: image.y() - amplitude * 2,
            easing: Konva.Easings.Linear,
            onFinish: () => {
              this.addVerticalAnimation(image); // 循环动画
            },
          });
          reverseTween.play();
        },
      });
      tween.play();
    },
    showTextBox(image: Konva.Image) {
      // 删除已存在的文本框
      if (this.textBox) {
        this.textBox.destroy();
        this.textBox = null;
      }

      if (this.textBoxText) {
        this.textBoxText.destroy();
        this.textBoxText = null;
      }

      const boxWidth = 100;
      const boxHeight = 100;
      let boxX = image.x() + image.width() * image.scaleX();
      let boxY = image.y();

      // 获取画布的尺寸
      const stage = this.layer.getStage();
      const canvasWidth = stage.width();
      const canvasHeight = stage.height();

      // 检查并调整文本框的位置
      if (boxX > 888) {
        boxX = boxX - 160; // 调整文本框的 x 坐标
      }

      if (boxY < 0) {
        boxY = 100 - Math.abs(boxY); // 调整文本框的 y 坐标
      }

      this.textBox = new Konva.Rect({
        x: boxX,
        y: boxY,
        width: boxWidth,
        height: boxHeight,
        fill: "white",
        stroke: "black",
        strokeWidth: 2,
      });

      this.textBoxText = new Konva.Text({
        x: boxX + 5,
        y: boxY + 5,
        text: "Hello, World!",
        fontSize: 14,
        fontFamily: "Arial",
        fill: "black",
      });

      this.layer.add(this.textBox);
      this.layer.add(this.textBoxText);
      this.layer.batchDraw();
    },
  },

  beforeUnmount() {
    // 在组件销毁前将数据保存到 localStorage
    localStorage.setItem("generatedImages", JSON.stringify(this.savedImages));
  },
});
</script>

<style lang="less" scoped>
#container {
  width: 100%;
  height: 100%;
  overflow: scroll;
}

#preview {
  position: absolute;
  top: 2px;
  right: 2px;
  border: 1px solid grey;
  background-color: lightgrey;
}
</style>
